import {SwapItem} from "../../types";
import {PhoneNumberRenderer} from "../app";
import {buildSwapTarget, findAllPhoneNumbers, getXPath, reformatPhoneNumber} from "./common";
import {SwapEvent} from "../events";
import {debug} from "../debugging";

// See phoneNumberPattern.txt for a breakdown of this regex
// Updated to match en-dash (–) and em-dash (—) in addition to regular hyphen (-)
export const phoneNumberPattern = new RegExp(/(?:(?:\+?([1-9]\d{0,2})\s*(?:[.\-–—]\s*)?)?)?(?:(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))|(?:\(([0-9]{3})\)\s*|([0-9]{3})))(?:\s*(?:[.\-–—]\s*)?(\d)\s*(?:[.\-–—]\s*)?(\d)\s*(?:[.\-–—]\s*)?(\d)(?:\s*(?:[.\-–—]\s*)?(\d))?(?:\s*(?:[.\-–—]\s*)?(\d))?(?:\s*(?:[.\-–—]\s*)?(\d))?(?:\s*(?:[.\-–—]\s*)?(\d))?(?:\s*(?:[.\-–—]\s*)?(\d))?(?:\s*(?:[.\-–—]\s*)?(\d))?|\s*(?:[.\-–—]\s*)?([A-Za-z0-9])\s*(?:[.\-–—]\s*)?([A-Za-z0-9])\s*(?:[.\-–—]\s*)?([A-Za-z0-9])(?:\s*(?:[.\-–—]\s*)?([A-Za-z0-9]))?(?:\s*(?:[.\-–—]\s*)?([A-Za-z0-9]))?(?:\s*(?:[.\-–—]\s*)?([A-Za-z0-9]))?(?:\s*(?:[.\-–—]\s*)?([A-Za-z0-9]))?)(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?(?=[A-Za-z]{0,5}[.\-–—\s]|$)/g);

export type FormattedPhoneNumber = {
    originalFormat: string,
    raw: string
}

let changes: (['href', Element, string, string] | ['text', Node, string, string])[] = [];

// we render with cached data initially, so we need to undo the initial render
// when we get new data
export const undoReplacements = () => {
    for (const [type, node, value] of changes) {
        if (type === 'text') {
            node.nodeValue = value;
        } else if (type === 'href') {
            node.setAttribute('href', value);
        }
    }

    changes = [];
}

// @ts-ignore
export const getMissingChanges = (): boolean => {
  return changes.reduce((carry, change) => {
      if (carry) {
          return true;
      }

      if (!document.body.contains(change[1])) {
          return false;
      }

      if (change[0] === 'href') {
          if (change[1] instanceof HTMLAnchorElement) {
              if (change[1].href !== change[3]) {
                  return true;
              }
          }
      } else {
          if (change[1] instanceof Node) {
              if (change[1].nodeValue !== change[3]) {
                  return true;
              }
          }
      }

      return false;
  }, false);
}

export const renderReplacement: PhoneNumberRenderer = (node: Node, swaps: SwapItem[]) => {
    const pendingEvents: CustomEvent[] = [];

    const replaceTextValue = (text: string) => {
        return findAllPhoneNumbers(text).reduce((result: string, phoneNumber: FormattedPhoneNumber): string => {

            const target = buildSwapTarget(phoneNumber.raw).toUpperCase();

            const swap = swaps.filter((swap) => swap.swapTargets.includes(target)).pop();

            if (swap === undefined) {
                return result;
            }

            const replacement = reformatPhoneNumber(phoneNumber.originalFormat, swap.number)

            debug(`swapping ${phoneNumber.raw} -> ${swap.number}`);

            pendingEvents.push(
                new CustomEvent<SwapEvent>('800dni.swap', {
                    detail: {
                        node,
                        replacement: replacement,
                        replacementE164: swap.number,
                        original: phoneNumber.originalFormat,
                        originalE164: target,
                        selector: getXPath(node.parentElement),
                    }
                })
            );

            return result.replace(
                phoneNumber.originalFormat,
                replacement,
            );
        }, text);
    }

    const replaceHrefAttribute = (node: Element) => {
        const href = node.getAttribute('href');

        if (href) {
            node.setAttribute(
                'href',
                replaceTextValue(decodeURI(href)),
            );

            changes.push([
                'href',
                node,
                href,
                replaceTextValue(decodeURI(href)),
            ])
        }
    }

    if (node instanceof HTMLAnchorElement) {
        replaceHrefAttribute(node);
    } else if (node.nodeType === Node.TEXT_NODE) {
        const replacement = replaceTextValue(String(node.nodeValue));

        if (replacement !== node.nodeValue) {
            changes.push([
                'text',
                node,
                String(node.nodeValue),
                replacement,
            ])

            node.nodeValue = replacement;

            if (node.parentElement) {
                replaceHrefAttribute(node.parentElement);
            }
        }
    } else if (node instanceof Element) {
        // Handle parent elements with phone numbers split across child nodes
        const originalText = node.textContent || '';
        const replacedText = replaceTextValue(originalText);

        if (replacedText !== originalText) {
            const originalHTML = node.innerHTML;

            // Create a temporary div with the original HTML (only once)
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = originalHTML;

            // Extract all phone numbers from the original text
            const phoneNumbers = findAllPhoneNumbers(originalText);

            phoneNumbers.forEach(phoneNumber => {
                const target = buildSwapTarget(phoneNumber.raw).toUpperCase();
                const swap = swaps.filter((swap) => swap.swapTargets.includes(target)).pop();

                if (swap) {
                    const replacement = reformatPhoneNumber(phoneNumber.originalFormat, swap.number);

                    // Find where the phone number appears in the original text
                    const phoneStartIndex = originalText.indexOf(phoneNumber.originalFormat);
                    const phoneEndIndex = phoneStartIndex + phoneNumber.originalFormat.length;

                    // Walk through all text nodes and track position in combined text
                    const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT);
                    let textNode: Text | null;

                    // Collect all text nodes first (since walker state can be affected by modifications)
                    const textNodes: Text[] = [];
                    while (textNode = walker.nextNode() as Text) {
                        textNodes.push(textNode);
                    }

                    // Now process each text node
                    let combinedPosition = 0;
                    textNodes.forEach(tn => {
                        const nodeValue = tn.nodeValue || '';
                        const nodeStart = combinedPosition;
                        const nodeEnd = combinedPosition + nodeValue.length;

                        // Check if this text node overlaps with the phone number
                        if (nodeEnd > phoneStartIndex && nodeStart < phoneEndIndex) {
                            // Calculate which part of this node overlaps with the phone number
                            const overlapStart = Math.max(0, phoneStartIndex - nodeStart);
                            const overlapEnd = Math.min(nodeValue.length, phoneEndIndex - nodeStart);

                            // Extract the parts
                            const before = nodeValue.substring(0, overlapStart);
                            const overlapping = nodeValue.substring(overlapStart, overlapEnd);
                            const after = nodeValue.substring(overlapEnd);

                            // Find the corresponding part in the replacement
                            const phoneOffset = nodeStart + overlapStart - phoneStartIndex;
                            const replacementPart = replacement.substring(phoneOffset, phoneOffset + overlapping.length);

                            tn.nodeValue = before + replacementPart + after;
                        }

                        combinedPosition += nodeValue.length;
                    });
                }
            });

            const newHTML = tempDiv.innerHTML;

            if (newHTML !== originalHTML) {
                node.innerHTML = newHTML;

                changes.push([
                    'text',
                    node as any,
                    originalHTML,
                    newHTML,
                ])
            }

            if (node.parentElement) {
                replaceHrefAttribute(node.parentElement);
            }
        }
    }
    
    for (const event of pendingEvents) {
        window.dispatchEvent(event);
    }
}
